import { Injectable, inject, computed, signal } from "@angular/core";
import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
} from "amazon-cognito-identity-js";
import { cognitoConfig } from "./cognito.config";
import { ApiService, Supplier } from "./api.service";

const TOKEN_KEY = "do_your_shades_supplier_token";
const REFRESH_KEY = "do_your_shades_supplier_refresh";
const SUPPLIER_KEY = "do_your_shades_supplier";
const LAST_ACTIVE_KEY = "do_your_shades_supplier_last_active";

export const IDLE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/** Thrown on first login when Cognito requires a new password (invited users). */
export class NewPasswordRequiredError extends Error {
  constructor(readonly complete: (newPassword: string) => Promise<void>) {
    super("NEW_PASSWORD_REQUIRED");
  }
}

function decodePayload(token: string): Record<string, unknown> | null {
  try {
    return JSON.parse(atob(token.split(".")[1])) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isExpired(token: string): boolean {
  const p = decodePayload(token);
  if (!p || typeof p["exp"] !== "number") return false;
  return Date.now() >= (p["exp"] as number) * 1000;
}

function loadSupplier(): Supplier | null {
  try {
    const raw = localStorage.getItem(SUPPLIER_KEY);
    return raw ? (JSON.parse(raw) as Supplier) : null;
  } catch {
    return null;
  }
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly api = inject(ApiService);
  private pool: CognitoUserPool | null;

  private tokenValue = localStorage.getItem(TOKEN_KEY);
  private refreshValue = localStorage.getItem(REFRESH_KEY);
  private supplierSignal = signal<Supplier | null>(loadSupplier());

  readonly supplier = this.supplierSignal.asReadonly();
  readonly isAuthed = computed(() => {
    const t = this.tokenValue;
    return t !== null && !isExpired(t);
  });

  constructor() {
    const { userPoolId, clientId } = cognitoConfig();
    this.pool =
      userPoolId && clientId
        ? new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId })
        : null;
  }

  private requirePool(): CognitoUserPool {
    if (!this.pool) {
      throw new Error("Auth is not configured.");
    }
    return this.pool;
  }

  token(): string | null {
    return this.tokenValue;
  }

  private persist(token: string, refreshToken: string, supplier: Supplier) {
    this.tokenValue = token;
    this.refreshValue = refreshToken;
    this.supplierSignal.set(supplier);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REFRESH_KEY, refreshToken);
    localStorage.setItem(SUPPLIER_KEY, JSON.stringify(supplier));
  }

  async login(email: string, password: string): Promise<void> {
    const details = new AuthenticationDetails({ Username: email, Password: password });
    const cognitoUser = new CognitoUser({ Username: email, Pool: this.requirePool() });
    // SRP (the library default) — the supplier app client allows USER_SRP_AUTH.
    await new Promise<void>((resolve, reject) => {
      cognitoUser.authenticateUser(details, {
        onSuccess: (s) => {
          this.finishLogin(this.sessionFrom(email, s)).then(resolve, reject);
        },
        onFailure: (e) => reject(e),
        // First sign-in for an invited supplier: Cognito forces a new password.
        newPasswordRequired: () =>
          reject(
            new NewPasswordRequiredError(
              (newPassword: string) =>
                new Promise<void>((res, rej) => {
                  cognitoUser.completeNewPasswordChallenge(
                    newPassword,
                    {},
                    {
                      onSuccess: (s) => {
                        this.finishLogin(this.sessionFrom(email, s)).then(
                          () => res(),
                          rej,
                        );
                      },
                      onFailure: (e) => rej(e),
                    },
                  );
                }),
            ),
          ),
      });
    });
  }

  private sessionFrom(
    email: string,
    s: {
      getIdToken: () => { payload: Record<string, unknown>; getJwtToken: () => string };
      getRefreshToken: () => { getToken: () => string };
    },
  ) {
    const payload = s.getIdToken().payload as Record<string, string>;
    return {
      idToken: s.getIdToken().getJwtToken(),
      refreshToken: s.getRefreshToken().getToken(),
      email: payload["email"] ?? email,
      sub: payload["sub"] ?? "",
      name: payload["name"] ?? "",
      role: payload["custom:role"] ?? "",
    };
  }

  private async finishLogin(session: {
    idToken: string;
    refreshToken: string;
    sub: string;
    role: string;
  }): Promise<void> {
    if (session.role !== "supplier") {
      await this.logout();
      throw new Error("This account is not a supplier.");
    }
    // Fetch the business record (supplier.id === cognito sub) for UI display.
    const me = await this.fetchMe(session.idToken);
    localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
    this.persist(session.idToken, session.refreshToken, me);
  }

  /** Change the signed-in supplier's Cognito password. Uses the session cached
   * by amazon-cognito-identity-js (refreshed automatically when expired). */
  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    const email = this.supplierSignal()?.email;
    if (!email) throw new Error("Not signed in.");
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: this.requirePool(),
    });
    await new Promise<void>((resolve, reject) => {
      cognitoUser.getSession((err: Error | undefined) => {
        if (err) reject(err);
        else resolve();
      });
    });
    await new Promise<void>((resolve, reject) => {
      cognitoUser.changePassword(
        currentPassword,
        newPassword,
        (err: Error | undefined) => {
          if (err) reject(err);
          else resolve();
        },
      );
    });
  }

  /** Silent refresh using the stored refresh token; false if unusable. */
  async restoreSession(): Promise<boolean> {
    const t = this.tokenValue;
    if (!t) return false;
    if (!isExpired(t)) return true;
    return this.refresh();
  }

  /** Refresh only if the current idToken is within `marginMs` of expiring. */
  async refreshIfNeeded(marginMs = 60_000): Promise<void> {
    const token = this.tokenValue;
    if (!token) return;
    const p = decodePayload(token);
    const exp = p?.["exp"];
    if (typeof exp !== "number") return;
    if (Date.now() >= (exp * 1000 - marginMs)) {
      await this.refresh();
    }
  }

  async refresh(): Promise<boolean> {
    const supplier = this.supplierSignal();
    const email = supplier?.email;
    if (!email || !this.refreshValue) {
      this.logout();
      return false;
    }
    const cognitoUser = new CognitoUser({ Username: email, Pool: this.requirePool() });
    const refreshToken = new CognitoRefreshToken({ RefreshToken: this.refreshValue });
    try {
      const session = await new Promise<{ idToken: string; refreshToken: string }>(
        (resolve, reject) => {
          cognitoUser.refreshSession(refreshToken, (err, s) => {
            if (err) {
              reject(err);
              return;
            }
            resolve({
              idToken: s.getIdToken().getJwtToken(),
              refreshToken: s.getRefreshToken().getToken(),
            });
          });
        },
      );
      this.persist(session.idToken, session.refreshToken, this.supplierSignal()!);
      return true;
    } catch {
      this.logout();
      return false;
    }
  }

  private fetchMe(idToken: string): Promise<Supplier> {
    return this.api.meWithToken(idToken);
  }

  idleExceeded(): boolean {
    if (!this.tokenValue) return false;
    const last = Number(localStorage.getItem(LAST_ACTIVE_KEY) ?? Date.now());
    return Date.now() - last > IDLE_MS;
  }

  touch() {
    localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
  }

  async logout() {
    const email = this.supplierSignal()?.email;
    if (email) {
      try {
        const cognitoUser = new CognitoUser({ Username: email, Pool: this.requirePool() });
        await new Promise<void>((resolve) =>
          cognitoUser.globalSignOut({
            onSuccess: () => resolve(),
            onFailure: () => resolve(),
          }),
        );
      } catch {
        // best-effort
      }
    }
    this.tokenValue = null;
    this.refreshValue = null;
    this.supplierSignal.set(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(SUPPLIER_KEY);
    localStorage.removeItem(LAST_ACTIVE_KEY);
  }
}
